import React from 'react';
import { Card, CardContent } from './ui/card';
import { TrendingUp, TrendingDown, Minus } from 'lucide-react';

interface StatCardProps {
  title: string;
  value: string | number;
  change?: number;
  data?: number[];
  prefix?: string;
  suffix?: string;
  icon?: React.ReactNode;
  variant?: 'default' | 'success' | 'warning' | 'error';
}

const StatCard: React.FC<StatCardProps> = ({
  title,
  value,
  change,
  data,
  prefix = '',
  suffix = '',
  icon,
  variant = 'default',
}) => {
  const isPositive = change !== undefined ? change > 0 : true;
  const isNeutral = change === 0;

  const getTrendColor = () => {
    if (isNeutral) return '#6b7280';
    return isPositive ? '#1FC78F' : '#E5484D';
  };

  const getVariantStyles = () => {
    switch (variant) {
      case 'success':
        return { iconBg: 'bg-emerald-500/10', iconColor: 'text-[#1FC78F]' };
      case 'warning':
        return { iconBg: 'bg-yellow-400/10', iconColor: 'text-[#FFCC00]' };
      case 'error':
        return { iconBg: 'bg-rose-500/10', iconColor: 'text-[#E5484D]' };
      default:
        return { iconBg: 'bg-primary/10', iconColor: 'text-primary' };
    }
  };

  const variantStyles = getVariantStyles();
  const trendColor = getTrendColor();

  // Lightweight SVG Sparkline Generator
  const renderSparkline = (points: number[]) => {
    if (!points || points.length < 2) return null;

    const width = 80;
    const height = 32;
    const max = Math.max(...points);
    const min = Math.min(...points);
    const range = max - min || 1;
    const stepX = width / (points.length - 1);

    const pathD = points
      .map((val, i) => {
        const x = i * stepX;
        const y = height - ((val - min) / range) * height;
        return `${i === 0 ? 'M' : 'L'} ${x},${y}`;
      })
      .join(' ');

    // Create gradient fill path
    const fillPath = `${pathD} L ${width},${height} L 0,${height} Z`;

    return (
      <svg
        width={width}
        height={height}
        viewBox={`0 0 ${width} ${height}`}
        style={{ overflow: 'visible' }}
      >
        <defs>
          <linearGradient id={`gradient-${title}`} x1="0%" y1="0%" x2="0%" y2="100%">
            <stop offset="0%" stopColor={trendColor} stopOpacity="0.2" />
            <stop offset="100%" stopColor={trendColor} stopOpacity="0" />
          </linearGradient>
        </defs>
        <path d={fillPath} fill={`url(#gradient-${title})`} />
        <path
          d={pathD}
          fill="none"
          stroke={trendColor}
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    );
  };

  const TrendIcon = isNeutral ? Minus : isPositive ? TrendingUp : TrendingDown;

  return (
    <Card className="h-full flex flex-col border-black/[0.06] rounded-xl transition-all duration-200 hover:border-black/[0.12] hover:shadow-[0_4px_12px_rgba(0,0,0,0.05)]">
      <CardContent className="p-2.5">
        <div className="flex justify-between items-start mb-2">
          <span className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">
            {title}
          </span>
          {icon && (
            <div
              className={`w-8 h-8 rounded-lg ${variantStyles.iconBg} flex items-center justify-center ${variantStyles.iconColor}`}
            >
              {icon}
            </div>
          )}
        </div>

        <div className="flex items-end justify-between">
          <div>
            <span className="text-[1.75rem] font-bold text-foreground leading-[1.2] tracking-tight">
              {prefix}
              {value}
              {suffix}
            </span>

            {change !== undefined && (
              <div className="flex items-center mt-1 gap-0.5">
                <span
                  className="flex items-center gap-0.5 px-1.5 py-0.5 rounded"
                  style={{ backgroundColor: `${trendColor}1a` }}
                >
                  <TrendIcon className="h-3.5 w-3.5" style={{ color: trendColor }} />
                  <span
                    className="text-xs font-semibold"
                    style={{ color: trendColor }}
                  >
                    {Math.abs(change)}%
                  </span>
                </span>
                <span className="text-xs text-muted-foreground">
                  vs last period
                </span>
              </div>
            )}
          </div>

          {data && <div className="ml-2">{renderSparkline(data)}</div>}
        </div>
      </CardContent>
    </Card>
  );
};

export default StatCard;
